/**
 * أخطاء موحّدة للتطبيق.
 * رسائل الأخطاء التي تُعرض للمستخدم لا تحتوي تفاصيل داخلية حساسة.
 * التفاصيل التقنية تُسجَّل في سجل التشخيص فقط.
 */

export type AppErrorCode =
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'validation'
  | 'conflict'
  | 'rate_limited'
  | 'storage_error'
  | 'network'
  | 'offline_queued'
  | 'unsupported'
  | 'unknown';

const DEFAULT_MESSAGES: Record<AppErrorCode, string> = {
  unauthenticated: 'الجلسة منتهية. يرجى تسجيل الدخول من جديد.',
  forbidden: 'لا تملك صلاحية تنفيذ هذه العملية.',
  not_found: 'العنصر المطلوب غير موجود.',
  validation: 'البيانات المدخلة غير صحيحة.',
  conflict: 'تم تعديل السجل من جهة أخرى. أعد التحميل ثم حاول مرة أخرى.',
  rate_limited: 'تمت محاولات كثيرة. حاول بعد قليل.',
  storage_error: 'تعذّر إتمام العملية على الملفات.',
  network: 'تعذّر الاتصال بالخدمة. تحقق من الشبكة.',
  offline_queued: 'لا يوجد اتصال. تم حفظ العملية محليًا وستُزامن تلقائيًا.',
  unsupported: 'هذه الوظيفة غير مدعومة في هذه البيئة.',
  unknown: 'حدث خطأ غير متوقع. إن تكرر، أبلغ مسؤول النظام.',
};

export class AppError extends Error {
  readonly code: AppErrorCode;
  readonly userMessage: string;
  readonly details: Record<string, unknown>;
  override readonly cause?: unknown;

  constructor(
    code: AppErrorCode,
    options: {
      userMessage?: string;
      details?: Record<string, unknown>;
      cause?: unknown;
      technicalMessage?: string;
    } = {},
  ) {
    super(options.technicalMessage ?? DEFAULT_MESSAGES[code]);
    this.name = 'AppError';
    this.code = code;
    this.userMessage = options.userMessage ?? DEFAULT_MESSAGES[code];
    this.details = options.details ?? {};
    if (options.cause !== undefined) this.cause = options.cause;
  }

  static forbidden(message?: string, details?: Record<string, unknown>): AppError {
    return new AppError('forbidden', { ...(message ? { userMessage: message } : {}), ...(details ? { details } : {}) });
  }
  static notFound(message?: string): AppError {
    return new AppError('not_found', message ? { userMessage: message } : {});
  }
  static validation(message: string, details?: Record<string, unknown>): AppError {
    return new AppError('validation', { userMessage: message, ...(details ? { details } : {}) });
  }
  static conflict(message?: string, details?: Record<string, unknown>): AppError {
    return new AppError('conflict', { ...(message ? { userMessage: message } : {}), ...(details ? { details } : {}) });
  }
  static unauthenticated(message?: string): AppError {
    return new AppError('unauthenticated', message ? { userMessage: message } : {});
  }
  static unsupported(message?: string): AppError {
    return new AppError('unsupported', message ? { userMessage: message } : {});
  }
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

/** تحويل أي خطأ إلى رسالة صالحة للعرض دون كشف التفاصيل الداخلية. */
export function toUserMessage(error: unknown): string {
  if (isAppError(error)) return error.userMessage;
  if (error instanceof Error) {
    // أخطاء الشبكة الشائعة
    if (/fetch|network|Failed to fetch/i.test(error.message)) {
      return DEFAULT_MESSAGES.network;
    }
  }
  return DEFAULT_MESSAGES.unknown;
}

export function errorCode(error: unknown): AppErrorCode {
  if (isAppError(error)) return error.code;
  if (error instanceof Error && /fetch|network/i.test(error.message)) return 'network';
  return 'unknown';
}
